import { useState, useEffect, useCallback, useRef } from 'react';

import { CustomerType } from '../types';

const CUSTOMER_TYPE_INTERVAL_DURATION = 30_000;

function getCustomerTypeFromQueryParams(): CustomerType | undefined {
  const customerType = new URLSearchParams(window.location.search).get(
    'category',
  );
  switch (customerType) {
    case 'commercial':
      return CustomerType.Commercial;
    case 'domestic':
      return CustomerType.Domestic;
    default:
      return;
  }
}

/**
 * Automatically toggles the customer type view every 30 seconds unless the "category"
 * query param is provided, in which case it'll remain fixed to the provided customer type.
 *
 * If the view is toggled outside of this hook (e.g. via the label switch) then
 * auto-switching will be disabled until the component is re-mounted.
 */
export function useAutoSwitchCustomerTypeState() {
  const [activeCustomerType, setActiveCustomerType] = useState(
    CustomerType.Commercial,
  );

  const [autoSwitchingEnabled, setAutoSwitchingEnabled] = useState(true);
  const intervalId = useRef<number>();

  useEffect(() => {
    if (autoSwitchingEnabled) {
      intervalId.current = window.setInterval(() => {
        // Only switch views when tab is considered active
        if (!document.hidden) {
          const newCustomerType =
            activeCustomerType === CustomerType.Commercial
              ? CustomerType.Domestic
              : CustomerType.Commercial;
          setActiveCustomerType(newCustomerType);
        }
      }, CUSTOMER_TYPE_INTERVAL_DURATION);
    }

    return () => {
      window.clearInterval(intervalId.current);
    };
  }, [activeCustomerType, autoSwitchingEnabled, intervalId]);

  const manuallySetActiveCustomerType = useCallback(
    (customerType: CustomerType) => {
      setAutoSwitchingEnabled(false);
      setActiveCustomerType(customerType);
      window.clearInterval(intervalId.current);
    },
    [intervalId],
  );

  useEffect(() => {
    const customerType = getCustomerTypeFromQueryParams();
    if (customerType) {
      manuallySetActiveCustomerType(customerType);
    }
  }, [manuallySetActiveCustomerType]);

  return {
    activeCustomerType,
    setActiveCustomerType: manuallySetActiveCustomerType,
  };
}
